"use client";

import { useState } from "react";
import { Notice, TextField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  MeaningCard,
  revealField,
  type WordRow,
  WordRows,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AntonymEntry,
  antonymEntries,
  antonymQuery,
  prefixCandidates,
  prefixedForms,
  prefixQuery,
  relatedEntries,
  relatedQuery,
} from "@/lib/tool-kit/vocabulary/antonyms";
import {
  answeredParam,
  normalizeLookup,
  type PartOfSpeech,
} from "@/lib/tool-kit/vocabulary/datamuse";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";

/** English strings. A translated content file overrides these through `ui`. */
export const ANTONYM_FINDER_STRINGS = {
  wordLabel: "Word",
  wordPlaceholder: "increase",
  wordHint:
    "Results appear as you type. Click any result to look up its antonyms in turn.",
  tryLabel: "Try:",
  examples: ["increase", "valid", "support", "biased", "theory"],
  emptyPrompt:
    "Type a word to see its antonyms — the words that mean the opposite.",
  posNames: {
    n: "noun",
    v: "verb",
    adj: "adjective",
    adv: "adverb",
    u: "other",
  } as Record<PartOfSpeech, string>,
  meaningLine: "{pos}: {definition}",
  heading: "Antonyms of “{word}”",
  antonymCount: "one:{count} antonym|other:{count} antonyms",
  none: "WordNet lists no antonym for “{word}”. Many words, nouns especially, have no true opposite — the sections below are alternatives, not antonyms.",
  prefixHeading: "Prefixed forms in the dictionary",
  prefixHint:
    "Adding un-, in-, dis-, or non- makes these real words, but a prefix does not always reverse the meaning — inflammable means the same as flammable. Check each definition.",
  relatedHeading: "Related words",
  relatedHint:
    "Words related in meaning to “{word}”. Most are near-synonyms and none is a listed antonym, though a few may work as opposites — check each definition.",
  noAlternatives:
    "No prefixed forms or related words either. Check the spelling, or try the base form — decide rather than deciding.",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  copy: "Copy",
  copied: "Copied!",
  copyWord: "Copy “{word}”",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof ANTONYM_FINDER_STRINGS;

const FIELD_ID = "ant-word";
const MAX_INPUT = 60;
const RE_LETTER = /\p{L}/u;

function rowsOf(entries: readonly AntonymEntry[], strings: Strings): WordRow[] {
  return entries.map((entry) => ({
    word: entry.word,
    badge: strings.posNames[entry.pos],
    detail: entry.definition ?? undefined,
  }));
}

/** The prefixed forms and related words shown when WordNet has no antonym. */
function Alternatives({
  word,
  onSelect,
  strings,
}: {
  word: string;
  onSelect: (word: string) => void;
  strings: Strings;
}) {
  const candidates = prefixCandidates(word);
  const lookup = useDatamuse(
    [relatedQuery(word), ...candidates.map(prefixQuery)],
    0
  );
  const answered = answeredParam(lookup.answered, "ml");
  const [related, ...answers] = lookup.results;
  const ready = lookup.status === "ready" && answered === word && related;
  const prefixed = ready ? prefixedForms(candidates, answers) : [];
  const shown = new Set(prefixed.map((entry) => entry.word));
  const similar = ready
    ? relatedEntries(word, related).filter((entry) => !shown.has(entry.word))
    : [];
  return (
    <>
      <LookupState
        failure={lookup.failure}
        onRetry={lookup.retry}
        status={lookup.status}
        strings={strings}
      />
      {ready && prefixed.length + similar.length === 0 ? (
        <Notice>{strings.noAlternatives}</Notice>
      ) : null}
      {prefixed.length > 0 ? (
        <section className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.prefixHeading}</h3>
          <p className={HINT}>{strings.prefixHint}</p>
          <WordRows
            copyStrings={strings}
            onSelect={onSelect}
            rows={rowsOf(prefixed, strings)}
          />
        </section>
      ) : null}
      {similar.length > 0 ? (
        <section className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.relatedHeading}</h3>
          <p className={HINT}>{t(strings.relatedHint, { word })}</p>
          <WordRows
            copyStrings={strings}
            onSelect={onSelect}
            rows={rowsOf(similar, strings)}
          />
        </section>
      ) : null}
    </>
  );
}

export default function AntonymFinderWidget() {
  const strings = useToolUi(ANTONYM_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const normalized = normalizeLookup(input);
  const word = RE_LETTER.test(normalized) ? normalized : "";
  const lookup = useDatamuse(word ? [antonymQuery(word)] : []);
  const answeredWord = answeredParam(lookup.answered, "rel_ant");
  const answer = word ? lookup.results[0] : undefined;
  const result = answer ? antonymEntries(answeredWord, answer) : null;
  const stale = lookup.status === "loading";

  function lookUp(next: string) {
    setInput(next);
    revealField(FIELD_ID);
  }

  return (
    <>
      <TextField
        hint={strings.wordHint}
        id={FIELD_ID}
        label={strings.wordLabel}
        onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
        placeholder={strings.wordPlaceholder}
        type="search"
        value={input}
      />
      {word ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompt}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples}
          />
        </div>
      )}
      {result ? (
        <div className={stale ? "space-y-5 opacity-60" : "space-y-5"}>
          <div aria-live="polite" className="space-y-1">
            <h2 className={WIDGET_H2}>
              {t(strings.heading, { word: answeredWord })}
            </h2>
            {result.entries.length > 0 ? (
              <p className={HINT}>
                {tPlural(strings.antonymCount, locale, result.entries.length, {
                  count: fmt(result.entries.length),
                })}
              </p>
            ) : null}
          </div>
          <MeaningCard
            echo={result.echo}
            meaningLine={strings.meaningLine}
            posNames={strings.posNames}
          />
          {result.entries.length > 0 ? (
            <WordRows
              copyStrings={strings}
              onSelect={lookUp}
              rows={rowsOf(result.entries, strings)}
            />
          ) : (
            <>
              <Notice>{t(strings.none, { word: answeredWord })}</Notice>
              {stale ? null : (
                <Alternatives
                  onSelect={lookUp}
                  strings={strings}
                  word={answeredWord}
                />
              )}
            </>
          )}
        </div>
      ) : null}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
